/**
 * Chamadas HTTP da frota.
 *
 * Papel: aqui ficam as quatro chamadas de `/api/veiculos` que a tela usa —
 * listar, criar, alterar e excluir um veículo.
 *
 * Como usar: a listagem devolve o array inteiro, e não uma página. Nenhuma
 * transformação acontece aqui — o que sai é o que o schema validou, inclusive a
 * placa já em maiúsculas e sem pontuação.
 */
import { type NovoVeiculo, type Veiculo, veiculoSchema } from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

/** A listagem inteira, sem envelope de paginação. */
const listaDeVeiculosSchema = z.array(veiculoSchema);

/**
 * Lê todos os veículos, em ordem de código.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns A frota cadastrada, possivelmente vazia.
 */
export function buscarVeiculos(sinal?: AbortSignal): Promise<Veiculo[]> {
  return clienteApi.get('/veiculos', { schema: listaDeVeiculosSchema, sinal });
}

/**
 * Cria um veículo.
 *
 * A API responde 409 quando o código ou a placa já estão na frota — a placa
 * repetida significa que aquele carro já está cadastrado com outro código.
 *
 * @param dados - O veículo preenchido, já validado por `novoVeiculoSchema`.
 * @returns O veículo como ficou gravado.
 */
export function criarVeiculo(dados: NovoVeiculo): Promise<Veiculo> {
  return clienteApi.post('/veiculos', dados, { schema: veiculoSchema });
}

/**
 * Substitui todos os campos de um veículo.
 *
 * @param id - Identificador da linha em `veiculos`.
 * @param dados - O veículo inteiro, e não apenas os campos alterados.
 * @returns O veículo como ficou gravado.
 */
export function atualizarVeiculo(id: string, dados: NovoVeiculo): Promise<Veiculo> {
  return clienteApi.put(`/veiculos/${encodeURIComponent(id)}`, dados, {
    schema: veiculoSchema,
  });
}

/**
 * Apaga um veículo.
 *
 * Não recebe schema porque a API responde 204 sem corpo. A API responde 409
 * quando o veículo ainda tem manutenção registrada, e nesse caso nada é
 * apagado — o histórico de gastos é justamente o que se quer preservar.
 *
 * @param id - Identificador da linha em `veiculos`.
 */
export function excluirVeiculo(id: string): Promise<void> {
  return clienteApi.remover(`/veiculos/${encodeURIComponent(id)}`);
}
